import { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import styles from './BMSLabelling.module.scss';
import useVersion from './useBmsVersion';
import ceSymbol from '../../assets/symbols/ce1912.png';
import udiSymbol from '../../assets/symbols/udi.png';
import eifuSymbol from '../../assets/symbols/eifu.png';
import manufacturerSymbol from '../../assets/symbols/manufacturer.png';
import medicalDeviceSymbol from '../../assets/symbols/md.png';
import Spinner from '~/components/Spinner';

export default function BMSLabelling() {
  const { data, isLoading, error } = useVersion();
  const { t } = useTranslation();

  // Group modules by moduleType
  const groupedModules: Record<BMSModuleType, BMSModule[]> = useMemo(() => {
    if (!data?.modules) return {} as Record<BMSModuleType, BMSModule[]>;

    return data.modules.reduce((acc, module) => {
      const type = module.moduleType;
      if (!acc[type]) {
        acc[type] = [];
      }
      acc[type].push(module);
      return acc;
    }, {} as Record<BMSModuleType, BMSModule[]>);
  }, [data?.modules]);

  const getModuleTypeKey = (moduleType: BMSModuleType) => {
    return moduleType === 'ADMINISTRATIVE_MODULE' ? 'administrative_module' : 'medical_device_module';
  };

  return (
    <div className={styles.wrapper}>
      {isLoading ? (
        <Spinner />
      ) : error ? (
        <div className="error-message font-inter">{t('bms.error_message')}</div>
      ) : (
        <>
          {data?.labellingInformation && data.labellingInformation.length > 0 && (
            <>
              {data.labellingInformation.map((section, i) => (
                <Fragment key={i}>
                  {section.header && <h2 className={styles.moduleTypeHeading}>{section.header}</h2>}
                  {section.body ? <ReactMarkdown>{section.body}</ReactMarkdown> : null}
                </Fragment>
              ))}
            </>
          )}

          {data?.client && (
            <div className={styles.clientSection}>
              <h3 className={styles.clientSectionHeading}>{t('bms.client.heading')}</h3>
              <p className={styles.clientSectionDescription}>{t('bms.client.description')}</p>

              <div className={styles.clientMetadata}>
                {data.client.system && (
                  <div className={styles.metadataField}>
                    <span className={styles.metadataLabel}>{t('bms.labels.interface')}</span>
                    <div className={styles.metadataValue}>
                      <span>{data.client.system}</span>
                    </div>
                  </div>
                )}
              </div>

              {data.client.labellingInformation?.map((section, i) => (
                <Fragment key={i}>
                  {section.header && <h3>{section.header}</h3>}
                  {section.body ? <ReactMarkdown>{section.body}</ReactMarkdown> : null}
                </Fragment>
              ))}
            </div>
          )}

          {/* BMS system and version */}
          {data?.system && data?.version && (
            <div className={styles.bmsHeader}>
              <div className={styles.metadataField}>
                <span className={styles.metadataLabel}>{t('bms.labels.version')}</span>
                <div className={styles.metadataValue}>
                  <span>{data.version}</span>
                </div>
              </div>
            </div>
          )}

          {data?.modules && data.modules.length > 0 && (
            <div className={styles.modulesSection}>
              {Object.entries(groupedModules).map(([moduleType, modules]) => {
                const typeKey = getModuleTypeKey(moduleType as BMSModuleType);
                return (
                  <div key={moduleType} className={styles.moduleTypeSection}>
                    <h3 className={styles.moduleTypeHeading}>{t(`bms.modules.${typeKey}.heading`)}</h3>
                    <p className={styles.moduleTypeDescription}>{t(`bms.modules.${typeKey}.description`)}</p>
                    {modules.map((module, index) => (
                      <div key={index} className={styles.moduleCard}>
                        {module.description && module.description.length > 0 && (
                          <div className={styles.moduleDescription}>
                            {module.description.map((section, idx) => (
                              <Fragment key={idx}>
                                {section.header && <h4>{section.header}</h4>}
                                {section.body ? <ReactMarkdown>{section.body}</ReactMarkdown> : null}
                              </Fragment>
                            ))}
                          </div>
                        )}

                        <div className={styles.moduleMetadata}>
                          {module.classification && (
                            <div className={styles.metadataField}>
                              <img src={medicalDeviceSymbol} alt="MD" className={styles.symbolIcon} />
                              <div className={styles.metadataValue}>
                                <span>
                                  {t('bms.labels.name')} {module.description[0]?.header}
                                </span>
                                <span>
                                  {t('bms.labels.classification')} {module.classification}
                                </span>
                                {module.version && (
                                  <span>
                                    {t('bms.labels.version')} {module.version}
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                          {module.manufacturer && (
                            <div className={styles.metadataField}>
                              <img src={manufacturerSymbol} alt="Manufacturer" className={styles.symbolIcon} />
                              <div className={styles.metadataValue}>
                                <ReactMarkdown>{module.manufacturer.replace(/\n/g, '  \n')}</ReactMarkdown>
                              </div>
                            </div>
                          )}
                          {module.udi && (
                            <div className={styles.metadataField}>
                              <img src={udiSymbol} alt="UDI" className={styles.symbolIcon} />
                              <div className={styles.metadataValue}>
                                <span>{module.udi}</span>
                              </div>
                            </div>
                          )}
                          {module.eifu && (
                            <div className={styles.metadataField}>
                              <a
                                href={module.eifu}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={styles.metadataValueLink}
                              >
                                <img src={eifuSymbol} alt="eIFU" className={styles.symbolIcon} />
                                <span className={styles.metadataUrl}>{module.eifu}</span>
                              </a>
                            </div>
                          )}
                          {module.ceMarkIdentifier && (
                            <div className={styles.metadataField}>
                              <img src={ceSymbol} alt="CE" className={styles.ceSymbol} />
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          )}

          {data?.compatibility === 'NOT_SUPPORTED' && (
            <div>
              <h2>{t('bms.compatibility_warning')}</h2>
            </div>
          )}

          {data?.system && data?.version && (
            <div className={styles.footer}>
              <p>{data.version}</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
